import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { DENTAL_CONSENT_PLANS_LAYOUT } from '../../constants/pricingPlans';

/**
 * Three-tier pricing cards — canonical SmileConsent comparison UI
 * (matches /dental-consent-plans).
 */
export function PricingComparisonCards({
  plans = DENTAL_CONSENT_PLANS_LAYOUT,
  renderCta,
  gridClassName = 'grid grid-cols-1 md:grid-cols-3 gap-8',
  cardClassName = '',
  animate = false,
}) {
  return (
    <div className={gridClassName}>
      {plans.map((plan) => {
        const isPopular = plan.popular;
        const isEnterprise = plan.id === 'enterprise' || plan.id === 'premium';

        const cardHoverClass = isPopular
          ? 'border-green-500 ring-2 ring-green-200 md:scale-[1.02] z-10 hover:border-green-600 hover:ring-green-300 hover:shadow-[0_20px_50px_rgba(34,197,94,0.25)] hover:md:scale-[1.05]'
          : isEnterprise
            ? 'border-gray-200 hover:border-indigo-500 hover:ring-2 hover:ring-indigo-100 hover:shadow-[0_20px_50px_rgba(99,102,241,0.18)]'
            : 'border-gray-200 hover:border-blue-500 hover:ring-2 hover:ring-blue-100 hover:shadow-[0_20px_50px_rgba(59,130,246,0.18)]';

        return (
        <div
          key={plan.id}
          className={`group relative bg-white border-2 rounded-2xl p-8 shadow-lg flex flex-col cursor-default
            transition-all duration-300 ease-out
            hover:-translate-y-2 hover:shadow-2xl
            ${cardHoverClass}
            ${animate ? 'animate-on-scroll' : ''} ${cardClassName}`}
        >
          {plan.popular && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-green-500 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-md">
                <TrophyIcon className="h-4 w-4" />
                Most Popular
              </div>
            </div>
          )}

          <div className="text-center mb-6 pt-1">
            <span className="inline-block text-xs font-semibold tracking-wide text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-3 transition-colors duration-300 group-hover:bg-blue-100">
              {plan.name}
            </span>
            <p className="text-gray-600 text-sm mb-1">{plan.description}</p>
            <p className="text-sm text-blue-600 font-semibold mb-4">{plan.consents}</p>
            <div className="flex items-baseline justify-center">
              <span className="text-5xl font-bold text-gray-900">
                {typeof plan.price === 'number' ? `£${plan.price}` : plan.price}
              </span>
              {plan.period && (
                <span className="text-gray-600 ml-2">/{plan.period}</span>
              )}
            </div>
          </div>

          <div className="space-y-3 mb-8 flex-1">
            {plan.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 leading-snug">{feature}</span>
              </div>
            ))}
          </div>

          {renderCta ? (
            renderCta(plan)
          ) : (
            <>
              <Link
                to="/register"
                className={`w-full py-3 px-6 rounded-xl font-semibold text-center block
                  transition-all duration-300 ease-out
                  hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${
                  plan.popular
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Start Free Trial
              </Link>
              <p className="text-xs text-gray-500 mt-2 text-center">Secure payment via Stripe</p>
            </>
          )}
        </div>
        );
      })}
    </div>
  );
}

export default function PricingComparisonSection({
  title = 'Simple, Transparent Pricing',
  subtitle = 'Choose the plan that fits your practice. All plans include a 14-day free trial.',
  plans,
  renderCta,
  showHeader = true,
  className = 'py-20 bg-white',
  containerClassName = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  headerClassName = 'text-center mb-16',
  animate = false,
  sectionRef,
  sectionId,
  children,
}) {
  return (
    <section ref={sectionRef} id={sectionId} className={className}>
      <div className={containerClassName}>
        {showHeader && (
          <div className={headerClassName}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h2>
            {subtitle && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{subtitle}</p>
            )}
          </div>
        )}
        {children}
        <PricingComparisonCards
          plans={plans}
          renderCta={renderCta}
          animate={animate}
        />
      </div>
    </section>
  );
}

/** Map billing / Stripe plan ids to comparison layout ids. */
export const STRIPE_TO_COMPARISON_PLAN_ID = {
  starter: 'starter_v2',
  starter_v2: 'starter_v2',
  professional: 'professional',
  premium: 'premium',
  growthpro: 'professional',
  'growth-pro': 'professional',
  enterprise: 'premium',
};

export function getComparisonPlanByStripeId(stripePlanId, plans = DENTAL_CONSENT_PLANS_LAYOUT) {
  const comparisonId = STRIPE_TO_COMPARISON_PLAN_ID[stripePlanId] || stripePlanId;
  return plans.find((p) => p.id === comparisonId);
}
